/**
 * Client-side password validation per UTD Security Standards (BHPD-5355).
 *
 * NOTE: This is a first-pass, user-facing check only. As of this change the
 * server enforces password reuse (BAD_PASSWORD) but applies no length or
 * complexity constraint of its own, so these length and complexity rules hold
 * in this browser build only. Any backend error returned on submit is still
 * surfaced to the user.
 *
 * Rules are intentionally broken into individual, single-responsibility checks
 * so that per-rule state can later be surfaced for specific inline feedback
 * without changing the core logic. For now, callers use a single generic
 * message (PASSWORD_ERROR_MESSAGE).
 */
import * as Yup from "yup";

export const MIN_PASSWORD_LENGTH = 12;

// Microsoft domain method: password must contain characters from 3 of these 4.
export const REQUIRED_CATEGORY_COUNT = 3;

export const PASSWORD_ERROR_MESSAGE =
  `Password must be at least ${MIN_PASSWORD_LENGTH} characters and contain characters from ` +
  `${REQUIRED_CATEGORY_COUNT} of the following: uppercase letters (A-Z), lowercase letters (a-z), ` +
  "numbers (0-9), special characters (e.g. !@#$%^&*)";

// --- Individual rule checks --------------------------------------------------

export const hasMinLength = (password: string): boolean => password.length >= MIN_PASSWORD_LENGTH;

export const hasUppercase = (password: string): boolean => /[A-Z]/.test(password);

export const hasLowercase = (password: string): boolean => /[a-z]/.test(password);

export const hasNumber = (password: string): boolean => /[0-9]/.test(password);

// Any character that is not an ASCII letter or digit counts as special. This
// broad set matches the "e.g. !@#$%^&*" wording in PASSWORD_ERROR_MESSAGE (the
// listed punctuation is an example, not the whole set) so the message and the
// accepted set describe the same thing.
export const hasSpecialChar = (password: string): boolean => /[^A-Za-z0-9]/.test(password);

/**
 * Number of the 4 complexity categories the password satisfies (0-4).
 */
export const countSatisfiedCategories = (password: string): number =>
  [hasUppercase, hasLowercase, hasNumber, hasSpecialChar].filter(check => check(password)).length;

/**
 * True when the password satisfies at least REQUIRED_CATEGORY_COUNT of the
 * 4 complexity categories.
 */
export const meetsComplexity = (password: string): boolean =>
  countSatisfiedCategories(password) >= REQUIRED_CATEGORY_COUNT;

/**
 * Combined check: length AND complexity.
 */
export const isValidPassword = (password: string): boolean =>
  hasMinLength(password) && meetsComplexity(password);

// --- Yup integration ---------------------------------------------------------

/**
 * Reusable Yup validator for password fields. The reset password screen plugs
 * this in so the rules stay in one place.
 *
 * A single generic message is used for now; individual rule state is available
 * via the exported check functions if/when specific feedback is added.
 */
export const yupPassword = (): Yup.StringSchema =>
  Yup.string().test("password-requirements", PASSWORD_ERROR_MESSAGE, (value): boolean =>
    isValidPassword(value ?? "")
  );
